import React, { useState, useEffect } from "react";
import { 
  X, Folder, Upload, Trash2, Clock, Loader2, Code2, Sparkles, Layers, Box, FileCode, Check, Copy
} from "lucide-react";
import { FaPython, FaJsSquare } from "react-icons/fa";
import { SiArduino } from "react-icons/si";
import { TbBrandCpp } from "react-icons/tb";
import { formatDistanceToNow } from "date-fns";
import { getSavedProjects, deleteProject } from "../services/projectService";
import { getSavedSnippets, deleteSnippet, getSavedSprites, deleteSprite, loadSnippetData } from "../services/backpackService";
import { reverseTranspileCode } from "../../../utils/transpiler/reverseTranspiler";
import { transpileCode } from "../../../utils/transpiler/codeTranspiler";
import ConvertSnippetDialog from "./ConvertSnippetDialog";
import S3ThumbnailImage from "./S3ThumbnailImage";
import { toast } from "react-toastify";

// Custom Backpack Icon SVG for a polished, educational aesthetic
const BackpackIcon = ({ className = "w-5 h-5 text-purple-600" }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M4 10a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10z" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    <path d="M8 21v-5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v5" />
    <circle cx="12" cy="11" r="1.5" />
  </svg>
);

const BackpackDrawer = ({
  isOpen,
  onClose,
  childId,
  environment = "scratch",
  theme = "dark",
  onLoadProject,
  onUploadLocal,
  onInsertSnippet,
  onInsertSprite,
}) => {
  const [activeTab, setActiveTab] = useState("projects"); // "projects" | "snippets" | "sprites"

  // Data States
  const [projects, setProjects] = useState([]);
  const [snippets, setSnippets] = useState([]);
  const [sprites, setSprites] = useState([]);

  // UI States
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [copiedSnippetId, setCopiedSnippetId] = useState(null);
  const [mismatchData, setMismatchData] = useState(null);
  const [deleteConfirmConfig, setDeleteConfirmConfig] = useState({
    isOpen: false,
    type: null, // "project" | "snippet" | "sprite"
    id: null,
    title: "",
    message: "",
  });

  const isLight = theme === "light";
  const isScratchWorkspace = environment === "scratch" || environment === "scratch_jr";

  const handleCopySnippet = async (e, snippet) => {
    e.stopPropagation();
    if (!snippet) return;
    try {
      let codeToCopy = snippet.code;
      if (!codeToCopy && snippet.filePath) {
        const loaded = await loadSnippetData(snippet);
        codeToCopy = loaded?.code;
      }
      if (!codeToCopy) return;
      navigator.clipboard.writeText(codeToCopy);
      setCopiedSnippetId(snippet.id);
      toast.success("Snippet copied to clipboard!");
      setTimeout(() => {
        setCopiedSnippetId(null);
      }, 2000);
    } catch (err) {
      console.warn("Failed to copy snippet code:", err);
    }
  };

  const handleSnippetClick = async (snippet) => {
    if (!snippet) return;

    const isBlock = snippet.environment === "scratch" || snippet.environment === "junior";
    const snippetLang = isBlock ? "scratch" : (snippet.language || snippet.environment || "python").toLowerCase();
    const currentEnv = (environment || "scratch").toLowerCase();

    // Guard 1: Block stack into Code workspace
    if (isBlock && (currentEnv !== "scratch" && currentEnv !== "scratch_jr" && currentEnv !== "junior")) {
      toast.warn("This is a Scratch block stack. Open the Scratch Visual Studio to insert blocks.");
      return;
    }

    // Guard 2: Code snippet into Scratch workspace
    if (!isBlock && (currentEnv === "scratch" || currentEnv === "scratch_jr" || currentEnv === "junior")) {
      toast.warn("This is a code snippet. Open the Python or JavaScript studio to use it.");
      return;
    }

    let loaded = snippet;
    if ((!isBlock && !snippet.code) || (isBlock && !snippet.snippetJson)) {
      try {
        loaded = await loadSnippetData(snippet);
      } catch (err) {
        toast.error("Failed to load snippet from cloud storage.");
        return;
      }
    }

    // Guard 3: Cross-language code snippet
    if (!isBlock && currentEnv !== "scratch" && currentEnv !== "scratch_jr" && currentEnv !== "junior") {
      if (snippetLang !== currentEnv) {
        let converted = "";
        if (snippetLang === "python" && (currentEnv === "javascript" || currentEnv === "cpp" || currentEnv === "arduino")) {
          converted = transpileCode({ sourceEnv: "python", targetLang: currentEnv, sourceCode: loaded?.code || "" });
        } else if (snippetLang === "javascript" && (currentEnv === "python" || currentEnv === "cpp" || currentEnv === "arduino")) {
          converted = transpileCode({ sourceEnv: "javascript", targetLang: currentEnv, sourceCode: loaded?.code || "" });
        } else {
          converted = reverseTranspileCode(loaded?.code || "", snippetLang, currentEnv);
        }

        setMismatchData({
          snippet: loaded,
          sourceLang: snippetLang,
          targetLang: currentEnv,
          convertedCode: converted,
        });
        return;
      }
    }

    // Direct insertion for matching language
    if (onInsertSnippet) onInsertSnippet(loaded);
    onClose();
  };

  const handleConfirmConvertAndInsert = () => {
    if (!mismatchData) return;
    const { snippet, targetLang, convertedCode } = mismatchData;
    if (onInsertSnippet) {
      onInsertSnippet({
        ...snippet,
        code: convertedCode,
        language: targetLang,
        name: snippet.name,
      });
    }
    setMismatchData(null);
    onClose();
  };

  const handleConfirmRawInsert = () => {
    if (!mismatchData) return;
    const { snippet, sourceLang } = mismatchData;
    if (onInsertSnippet) {
      onInsertSnippet(snippet);
    }
    toast.info(`Inserted raw ${sourceLang === "javascript" ? "JavaScript" : "Python"} snippet.`);
    setMismatchData(null);
    onClose();
  };

  // Reset tab to projects if active tab is sprites but workspace is not scratch
  useEffect(() => {
    if (!isScratchWorkspace && activeTab === "sprites") {
      setActiveTab("projects");
    }
  }, [environment, isScratchWorkspace, activeTab]);

  // Fetch data when drawer opens or tab/childId changes
  useEffect(() => {
    if (!isOpen || !childId) return;

    if (activeTab === "projects") {
      fetchProjects();
    } else if (activeTab === "snippets") {
      fetchSnippets();
    } else if (activeTab === "sprites" && isScratchWorkspace) {
      fetchSprites();
    }
  }, [isOpen, childId, environment, activeTab]); // eslint-disable-line

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const fetchedProjects = await getSavedProjects(childId, null);
      setProjects(fetchedProjects || []);
    } catch (error) {
      console.error("Failed to fetch projects", error);
      toast.error("Failed to load saved projects.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSnippets = async () => {
    setLoading(true);
    try {
      const fetchedSnippets = await getSavedSnippets(childId);
      const relevantSnippets = (fetchedSnippets || []).filter((s) => {
        const isBlock = s.environment === "scratch" || s.environment === "junior";
        return isScratchWorkspace ? isBlock : !isBlock;
      });
      setSnippets(relevantSnippets);
    } catch (error) {
      console.error("Failed to fetch snippets", error);
      toast.error("Failed to load snippets.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSprites = async () => {
    setLoading(true);
    try {
      const fetchedSprites = await getSavedSprites(childId);
      setSprites(fetchedSprites || []);
    } catch (error) {
      console.error("Failed to fetch sprites", error);
      toast.error("Failed to load saved sprites.");
    } finally {
      setLoading(false);
    }
  };

  // --- Delete Prompt Triggers ---
  const promptDeleteProject = (e, project) => {
    e.stopPropagation();
    setDeleteConfirmConfig({
      isOpen: true,
      type: "project",
      id: project.id,
      title: "Delete Project?",
      message: `Are you sure you want to delete "${project.projectName || "Unnamed Project"}"? This action cannot be undone.`,
    });
  };

  const promptDeleteSnippet = (e, snippet) => {
    e.stopPropagation();
    setDeleteConfirmConfig({
      isOpen: true,
      type: "snippet",
      id: snippet.id,
      title: "Delete Snippet?",
      message: `Are you sure you want to delete "${snippet.name || "Unnamed Snippet"}"? This action cannot be undone.`,
    });
  };

  const promptDeleteSprite = (e, sprite) => {
    e.stopPropagation();
    setDeleteConfirmConfig({
      isOpen: true,
      type: "sprite",
      id: sprite.id,
      title: "Delete Sprite?",
      message: `Are you sure you want to delete "${sprite.name || "Unnamed Sprite"}"? This action cannot be undone.`,
    });
  };

  const handleConfirmDelete = async () => {
    const { type, id } = deleteConfirmConfig;
    if (!id || !type) return;

    setDeletingId(id);
    setDeleteConfirmConfig((prev) => ({ ...prev, isOpen: false }));

    try {
      if (type === "project") {
        await deleteProject(id, childId);
        setProjects((prev) => prev.filter((p) => p.id !== id));
        toast.success("Project deleted successfully");
      } else if (type === "snippet") {
        await deleteSnippet(childId, id);
        setSnippets((prev) => prev.filter((s) => s.id !== id));
        toast.success("Snippet deleted successfully");
      } else if (type === "sprite") {
        await deleteSprite(childId, id);
        setSprites((prev) => prev.filter((s) => s.id !== id));
        toast.success("Sprite deleted successfully");
      }
    } catch (error) {
      console.error(`Failed to delete ${type}:`, error);
      toast.error(`Failed to delete ${type}`);
    } finally {
      setDeletingId(null);
    }
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "Just now";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "Just now";
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) return `${diffInMonths}mo ago`;
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears}y ago`;
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-xs z-[1040] transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Drawer Container */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] shadow-2xl z-[1050] transform transition-transform duration-300 ease-in-out flex flex-col font-sans border-0 ${
          isLight 
            ? "bg-white text-neutral-800 border-l border-neutral-200" 
            : "bg-[#0c0d12] text-white border-l border-[#222226]"
        } ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Top Header */}
        <div
          className={`flex items-center justify-between p-4 border-0 ${
            isLight ? "bg-neutral-50 border-b border-neutral-200" : "bg-[#0c0d12] border-b border-[#222226]"
          }`}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-purple-600/15 ring-1 ring-purple-500/25 flex items-center justify-center flex-shrink-0 border-0">
              <BackpackIcon className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className={`font-black text-base leading-tight ${isLight ? "text-neutral-800" : "text-white"}`}>
                My Backpack
              </h2>
              <span className={`text-[11px] font-semibold ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                {environment === "scratch" ? "Scratch Visual Studio" : environment === "python" ? "Python Studio" : environment === "javascript" ? "JavaScript Studio" : "Coding Studio"}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer border-0 ${
              isLight ? "bg-neutral-100 hover:bg-neutral-200 text-neutral-600" : "bg-white/5 hover:bg-white/10 text-neutral-300"
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 3-Tab Pill-Shaped Segmented Switcher */}
        <div className={`p-3 border-0 ${isLight ? "bg-neutral-50 border-b border-neutral-200" : "bg-[#0a0b0f] border-b border-[#222226]"}`}>
          <div className={`flex rounded-full p-1 gap-1 border-0 ${
            isLight 
              ? "bg-[#edf2f7] ring-1 ring-neutral-200 shadow-inner" 
              : "bg-[#14161f] ring-1 ring-[#27272a]"
          }`}>
            {/* Tab 1: Projects */}
            <button
              type="button"
              onClick={() => setActiveTab("projects")}
              className={`flex-1 py-1.5 px-3 rounded-full text-xs font-bold flex items-center justify-center gap-1.5 transition-all duration-200 cursor-pointer border-0 ${
                activeTab === "projects"
                  ? "bg-purple-600 text-white shadow-md shadow-purple-600/30"
                  : isLight
                    ? "text-neutral-600 hover:text-purple-700 hover:bg-white/60"
                    : "text-neutral-400 hover:text-white hover:bg-white/10"
              }`}
            >
              <Folder className="w-3.5 h-3.5" />
              <span>Projects</span>
            </button>

            {/* Tab 2: Snippets */}
            <button
              type="button"
              onClick={() => setActiveTab("snippets")}
              className={`flex-1 py-1.5 px-3 rounded-full text-xs font-bold flex items-center justify-center gap-1.5 transition-all duration-200 cursor-pointer border-0 ${
                activeTab === "snippets"
                  ? "bg-purple-600 text-white shadow-md shadow-purple-600/30"
                  : isLight
                    ? "text-neutral-600 hover:text-purple-700 hover:bg-white/60"
                    : "text-neutral-400 hover:text-white hover:bg-white/10"
              }`}
            >
              <Code2 className="w-3.5 h-3.5" />
              <span>Snippets</span>
            </button>

            {/* Tab 3: Sprites (Scratch Workspace Only) */}
            {isScratchWorkspace && (
              <button
                type="button"
                onClick={() => setActiveTab("sprites")}
                className={`flex-1 py-1.5 px-3 rounded-full text-xs font-bold flex items-center justify-center gap-1.5 transition-all duration-200 cursor-pointer border-0 ${
                  activeTab === "sprites"
                    ? "bg-purple-600 text-white shadow-md shadow-purple-600/30"
                    : isLight
                      ? "text-neutral-600 hover:text-purple-700 hover:bg-white/60"
                      : "text-neutral-400 hover:text-white hover:bg-white/10"
                }`}
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>Sprites</span>
              </button>
            )}
          </div>
        </div>

        {/* Content Body: Tab Panels */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
          
          {/* ========================================================================= */}
          {/* TAB 1: PROJECTS */}
          {/* ========================================================================= */}
          {activeTab === "projects" && (
            <div className="flex flex-col gap-3">
              {/* Load Local File Action */}
              <button
                onClick={() => {
                  onClose();
                  if (onUploadLocal) onUploadLocal();
                }}
                className={`w-full py-2.5 rounded-xl text-xs sm:text-sm font-bold shadow-xs flex items-center justify-center gap-2 transition cursor-pointer group border-0 ${
                  isLight
                    ? "bg-neutral-100 hover:bg-neutral-200 ring-1 ring-neutral-200 text-neutral-800"
                    : "bg-[#14161f] hover:bg-[#1a1d28] ring-1 ring-[#27272a] hover:ring-[#3f3f46] text-white"
                }`}
              >
                <Upload className="w-4 h-4 text-purple-400 group-hover:text-purple-300 transition-colors" />
                <span>Load from Local System</span>
              </button>

              <div className="flex items-center justify-between mt-1 mb-1">
                <h3 className={`text-xs font-bold uppercase tracking-wider ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                  Saved Projects ({projects.length})
                </h3>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center h-48 gap-3">
                  <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                  <p className={`text-sm font-medium ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                    Loading projects...
                  </p>
                </div>
              ) : projects.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-52 gap-3 text-center opacity-80 p-4 border-0 ring-1 ring-dashed ring-neutral-300 dark:ring-[#27272a] rounded-2xl bg-[#14161f]/30">
                  <Folder className={`w-12 h-12 ${isLight ? "text-neutral-400" : "text-neutral-500"}`} />
                  <div>
                    <p className={`text-sm font-bold ${isLight ? "text-neutral-700" : "text-neutral-300"}`}>
                      No saved projects yet
                    </p>
                    <p className={`text-xs mt-1 ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                      Save your work anytime using the Save button or Ctrl+S.
                    </p>
                  </div>
                </div>
              ) : (
                projects.map((project, index) => {
                  const projEnv = project.environment || (project.language === "python" ? "python" : project.language === "javascript" ? "javascript" : "scratch");
                  const isProjBlock = projEnv === "scratch" || projEnv === "junior";
                  const isProjJs = projEnv === "javascript";

                  return (
                    <div
                      key={project.id}
                      onClick={() => {
                        if (onLoadProject) onLoadProject(project);
                        onClose();
                      }}
                      title="Open project in workspace"
                      className={`group relative shadow-xs hover:-translate-y-0.5 rounded-xl p-3.5 flex items-center justify-between cursor-pointer transition-all duration-200 border-0 ${
                        isLight
                          ? "bg-[#f8fafc] hover:bg-purple-50/70 ring-1 ring-neutral-200"
                          : "bg-[#12141a] hover:bg-[#181a22] ring-1 ring-[#27272a] hover:ring-[#3f3f46] shadow-black/60"
                      }`}
                    >
                      <div className="flex items-center gap-3 overflow-hidden flex-1">
                        <S3ThumbnailImage
                          thumbnailPath={project.thumbnailPath || project.thumbnailUrl}
                          alt={project.projectName}
                          className="w-10 h-10 rounded-lg overflow-hidden ring-1 ring-neutral-200 dark:ring-neutral-700 flex-shrink-0 bg-black/10 flex items-center justify-center"
                          fallback={
                            <span className={`text-xs font-bold w-5 flex-shrink-0 text-center ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>
                              {index + 1}.
                            </span>
                          }
                        />

                        <div className="flex flex-col overflow-hidden gap-1 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className={`font-bold text-sm truncate ${isLight ? "text-neutral-900" : "text-white"}`} title={project.projectName}>
                              {project.projectName || "Unnamed Project"}
                            </h3>
                            <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md flex items-center gap-1 border-0 ${
                              isProjBlock
                                ? "bg-amber-500/15 text-amber-600 dark:text-amber-400 ring-1 ring-amber-500/30"
                                : isProjJs
                                ? "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 ring-1 ring-yellow-500/30"
                                : "bg-blue-500/15 text-blue-600 dark:text-blue-400 ring-1 ring-blue-500/30"
                            }`}>
                              {isProjBlock ? (
                                <>
                                  <Layers className="w-3 h-3 text-amber-400" />
                                  <span>SCRATCH</span>
                                </>
                              ) : isProjJs ? (
                                <>
                                  <FaJsSquare className="w-3 h-3 text-yellow-400" />
                                  <span>JAVASCRIPT</span>
                                </>
                              ) : (
                                <>
                                  <FaPython className="w-3 h-3 text-blue-400" />
                                  <span>PYTHON</span>
                                </>
                              )}
                            </span>
                          </div>

                          <div className={`flex items-center gap-1.5 text-xs font-medium ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>{formatRelativeTime(project.updatedAt)}</span>
                            </div>
                            {project.isMultiFile && (
                              <>
                                <span className="opacity-40">•</span>
                                <span className={`px-1.5 py-0.2 rounded text-[10px] font-semibold ${isLight ? "bg-purple-100 text-purple-700" : "bg-purple-500/20 text-purple-300"}`}>
                                  {project.fileCount} files
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={(e) => promptDeleteProject(e, project)}
                        disabled={deletingId === project.id}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-red-400 hover:bg-red-500/10 flex-shrink-0 transition-colors cursor-pointer disabled:opacity-50 border-0"
                        title="Delete Project"
                      >
                        {deletingId === project.id ? (
                          <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* TAB 2: SNIPPETS */}
          {/* ========================================================================= */}
          {activeTab === "snippets" && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between mb-1">
                <h3 className={`text-xs font-bold uppercase tracking-wider ${isLight ? "text-slate-500" : "text-white/60"}`}>
                  Saved Snippets ({snippets.length})
                </h3>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center h-48 gap-3">
                  <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                  <p className={`text-sm font-medium ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                    Loading snippets...
                  </p>
                </div>
              ) : snippets.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-56 gap-3 text-center p-6 border border-dashed rounded-2xl border-slate-300 dark:border-slate-800">
                  <div className="w-12 h-12 rounded-2xl bg-purple-600/10 flex items-center justify-center text-purple-600">
                    <Code2 className="w-6 h-6" />
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${isLight ? "text-slate-800" : "text-slate-200"}`}>
                      No Snippets Saved Yet
                    </p>
                    <p className={`text-xs mt-1.5 max-w-xs leading-relaxed ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                      {environment === "scratch" || environment === "scratch_jr"
                        ? "Right-click on any block stack in your workspace and choose 'Save to Backpack' to reuse it across projects."
                        : "Select any code in the editor, right-click, and choose 'Save as Snippet' (or press Ctrl+Shift+B) to reuse it anytime."}
                    </p>
                  </div>
                </div>
              ) : (
                snippets.map((snippet) => {
                  const isBlock = snippet.environment === "scratch" || snippet.environment === "junior";
                  const isJs = snippet.language === "javascript";
                  const isCpp = snippet.language === "cpp";
                  const isArduino = snippet.language === "arduino";

                  return (
                    <div
                      key={snippet.id}
                      onClick={() => handleSnippetClick(snippet)}
                      title="Insert snippet at cursor"
                      className={`group relative shadow-xs hover:-translate-y-0.5 rounded-xl p-3.5 flex flex-col gap-2.5 cursor-pointer transition-all duration-200 border-0 ${
                        isLight
                          ? "bg-[#f8fafc] hover:bg-purple-50/70 ring-1 ring-neutral-200 hover:ring-purple-300"
                          : "bg-[#12141a] hover:bg-[#181a22] ring-1 ring-[#27272a] hover:ring-[#3f3f46] shadow-black/60"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex flex-col gap-1.5 overflow-hidden flex-1">
                          {/* Main Title + Language Tag */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className={`font-bold text-sm truncate ${isLight ? "text-neutral-900" : "text-white"}`} title={snippet.name}>
                              {snippet.name}
                            </h3>
                            <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md flex items-center gap-1 border-0 ${
                              isBlock
                                ? "bg-amber-500/15 text-amber-600 dark:text-amber-400 ring-1 ring-amber-500/30"
                                : isJs
                                ? "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 ring-1 ring-yellow-500/30"
                                : isCpp
                                ? "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 ring-1 ring-cyan-500/30"
                                : isArduino
                                ? "bg-teal-500/15 text-teal-600 dark:text-teal-400 ring-1 ring-teal-500/30"
                                : "bg-blue-500/15 text-blue-600 dark:text-blue-400 ring-1 ring-blue-500/30"
                            }`}>
                              {isBlock ? (
                                <>
                                  <Layers className="w-3 h-3 text-amber-400" />
                                  <span>BLOCKS</span>
                                </>
                              ) : isJs ? (
                                <>
                                  <FaJsSquare className="w-3 h-3 text-yellow-400" />
                                  <span>JAVASCRIPT</span>
                                </>
                              ) : isCpp ? (
                                <>
                                  <TbBrandCpp className="w-3 h-3 text-cyan-400" />
                                  <span>C++</span>
                                </>
                              ) : isArduino ? (
                                <>
                                  <SiArduino className="w-3 h-3 text-teal-400" />
                                  <span>ARDUINO</span>
                                </>
                              ) : (
                                <>
                                  <FaPython className="w-3 h-3 text-blue-400" />
                                  <span>PYTHON</span>
                                </>
                              )}
                            </span>
                          </div>

                          {snippet.description && (
                            <p className={`text-xs line-clamp-2 ${isLight ? "text-neutral-600" : "text-neutral-400"}`}>
                              {snippet.description}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!isBlock && snippet.code && (
                            <button
                              type="button"
                              onClick={(e) => handleCopySnippet(e, snippet)}
                              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors cursor-pointer border-0 ${
                                isLight ? "hover:bg-neutral-200 text-neutral-500" : "hover:bg-white/10 text-neutral-400 hover:text-white"
                              }`}
                              title="Copy snippet code"
                            >
                              {copiedSnippetId === snippet.id ? (
                                <Check className="w-3.5 h-3.5 text-emerald-400" />
                              ) : (
                                <Copy className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={(e) => promptDeleteSnippet(e, snippet)}
                            disabled={deletingId === snippet.id}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer disabled:opacity-50 border-0"
                            title="Delete Snippet"
                          >
                            {deletingId === snippet.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-500" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Code Preview Box for Code Snippets */}
                      {!isBlock && snippet.code && (
                        <div className={`p-2.5 rounded-lg text-[11px] font-mono leading-relaxed max-h-20 overflow-hidden text-ellipsis whitespace-pre-wrap select-none border-0 ${
                          isLight
                            ? "bg-neutral-100/90 ring-1 ring-neutral-200 text-neutral-700"
                            : "bg-[#090a0d] ring-1 ring-[#222226] text-emerald-300/90"
                        }`}>
                          {snippet.code.slice(0, 160)}{snippet.code.length > 160 ? "..." : ""}
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-0.5">
                        <div className={`flex items-center gap-1 text-[11px] font-medium ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>
                          <Clock className="w-3 h-3" />
                          <span>{formatRelativeTime(snippet.updatedAt)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ========================================================================= */}
          {/* SPRITES TAB CONTENT (Scratch Only) */}
          {/* ========================================================================= */}
          {activeTab === "sprites" && isScratchWorkspace && (
            <div className="p-4 flex flex-col gap-3">
              {sprites.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-purple-500/10 ring-1 ring-purple-500/20 flex items-center justify-center text-purple-400 shadow-inner">
                    <Box className="w-6 h-6" />
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${isLight ? "text-slate-800" : "text-slate-200"}`}>
                      No Sprites Saved Yet
                    </p>
                    <p className={`text-xs mt-1.5 max-w-xs leading-relaxed ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                      Right-click on any sprite in your Scratch workspace and choose "Save to Backpack" to reuse it across all your projects.
                    </p>
                  </div>
                </div>
              ) : (
                sprites.map((sprite) => (
                  <div
                    key={sprite.id}
                    onClick={() => {
                      if (onInsertSprite) onInsertSprite(sprite);
                      onClose();
                    }}
                    className={`group relative shadow-xs hover:-translate-y-0.5 rounded-xl p-3 flex items-center justify-between cursor-pointer transition-all duration-200 border-0 ${
                      isLight
                        ? "bg-[#f8fafc] hover:bg-purple-50/70 ring-1 ring-neutral-200"
                        : "bg-[#12141a] hover:bg-[#181a22] ring-1 ring-[#27272a] hover:ring-[#3f3f46] shadow-black/60"
                    }`}
                  >
                    <div className="flex items-center gap-3 overflow-hidden flex-1">
                      {/* Costume Thumbnail Preview */}
                      <S3ThumbnailImage
                        thumbnailPath={sprite.thumbnailPath || sprite.thumbnailUrl || sprite.thumbnail}
                        alt={sprite.name}
                        className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-950/25 ring-1 ring-purple-500/20 flex items-center justify-center overflow-hidden flex-shrink-0 border-0"
                        imgClassName="w-10 h-10 object-contain"
                        fallback={<Box className="w-6 h-6 text-purple-400" />}
                      />

                      <div className="flex flex-col overflow-hidden">
                        <h3 className={`font-bold text-sm truncate ${isLight ? "text-neutral-900" : "text-white"}`} title={sprite.name}>
                          {sprite.name}
                        </h3>
                        {sprite.description && (
                          <p className={`text-xs truncate ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                            {sprite.description}
                          </p>
                        )}
                        <div className={`flex items-center gap-1 mt-0.5 text-xs font-medium ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>
                          <Clock className="w-3 h-3" />
                          <span>{formatRelativeTime(sprite.updatedAt)}</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={(e) => promptDeleteSprite(e, sprite)}
                      disabled={deletingId === sprite.id}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-red-400 hover:bg-red-500/10 flex-shrink-0 transition-colors cursor-pointer disabled:opacity-50 border-0"
                      title="Delete Sprite"
                    >
                      {deletingId === sprite.id ? (
                        <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Custom Delete Confirmation Modal */}
      {deleteConfirmConfig.isOpen && (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/75 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div
            className={`rounded-2xl w-full max-w-sm flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 border-0 ${
              isLight
                ? "bg-white ring-1 ring-neutral-200 shadow-xl text-neutral-900"
                : "bg-[#13151b] ring-1 ring-[#27272a] shadow-[0_20px_50px_rgba(0,0,0,0.8)] text-white"
            }`}
          >
            <div className="p-4 flex flex-col gap-2">
              <div className="flex items-start justify-between mb-1">
                <h3
                  className="font-bold text-base"
                  style={{ color: isLight ? "#111827" : "#ffffff" }}
                >
                  {deleteConfirmConfig.title}
                </h3>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmConfig((prev) => ({ ...prev, isOpen: false }))}
                  className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors border-0 cursor-pointer ${
                    isLight ? "text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700" : "text-neutral-400 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <p
                className="text-xs leading-relaxed m-0"
                style={{ color: isLight ? "#374151" : "#e2e8f0" }}
              >
                {deleteConfirmConfig.message}
              </p>
            </div>

            <div
              className={`px-4 py-3 flex items-center justify-end gap-2.5 border-0 ${
                isLight ? "bg-neutral-50 border-t border-neutral-100" : "bg-[#0c0d12] border-t border-[#222226]"
              }`}
            >
              <button
                type="button"
                onClick={() => setDeleteConfirmConfig((prev) => ({ ...prev, isOpen: false }))}
                className="px-3.5 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer border-0"
                style={{ color: isLight ? "#4b5563" : "#cbd5e1" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-[#ef4444] hover:bg-[#dc2626] text-white shadow-md shadow-red-600/30 transition-colors cursor-pointer border-0"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Smart Cross-Language Conversion Dialog */}
      <ConvertSnippetDialog
        isOpen={!!mismatchData}
        onClose={() => setMismatchData(null)}
        snippet={mismatchData?.snippet}
        sourceLang={mismatchData?.sourceLang}
        targetLang={mismatchData?.targetLang}
        convertedCode={mismatchData?.convertedCode}
        onConfirmConvertAndInsert={handleConfirmConvertAndInsert}
        onConfirmRawInsert={handleConfirmRawInsert}
        theme={theme}
      />
    </>
  );
};

export default BackpackDrawer;
